import {
    formatDateWithTimezone,
    formatTimeAndTimezone,
} from "@impero-com/common/utils/formatting";
import { Trans, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { CollapseContentSection, Svg, Tag } from "@impero-com/ui";
import { cx } from "class-variance-authority";
import { Link } from "react-router-dom";

import arrowRightSvg from "img/arrow-right-md.svg";
import settingsSvg from "img/settings.svg";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import * as theme from "./RecentChanges.css";

export const MAX_RECENT_CHANGES = 3;

export type RecentChangeActor =
    | {
          type: "user";
          name: string;
          /** Where the change opens, such as its event log entry. */
          url: string;
      }
    | {
          type: "automated";
          /** Ends the sentence, e.g. "during the policy migration". */
          reason: string;
      };

export interface RecentChange {
    /** The event's id. */
    id: number;
    /** ISO 8601 date-time. */
    created: string;
    actor: RecentChangeActor;
    /** The changed field's name, such as a policy. */
    field: string;
    /** Left out when there is no old and new value, such as for a creation. */
    values?: { from: string; to: string };
}

interface RecentChangesProps {
    changes: readonly RecentChange[];
    emptyLabel: string;
    eventLogUrl: string;
}

export function RecentChanges({
    changes,
    emptyLabel,
    eventLogUrl,
}: RecentChangesProps) {
    const { t } = useTranslation();
    const hasChanges = changes.length > 0;

    return (
        <CollapseContentSection
            styleVariant="primary"
            level={4}
            noPadding
            titleLabel={t("RecentChanges_Title")}
        >
            {hasChanges ? (
                <>
                    <ul className={theme.list}>
                        {changes.slice(0, MAX_RECENT_CHANGES).map((change) => (
                            <RecentChangeRow key={change.id} change={change} />
                        ))}
                    </ul>
                    <div className={theme.footer}>
                        <Link to={eventLogUrl} className={theme.eventLog}>
                            {t("RecentChanges_GoToAuditTrails")}
                        </Link>
                    </div>
                </>
            ) : (
                <p className={theme.empty}>{emptyLabel}</p>
            )}
        </CollapseContentSection>
    );
}

interface RecentChangeRowProps {
    change: RecentChange;
}

function RecentChangeRow({ change }: RecentChangeRowProps) {
    const timezone = { name: useCurrentUserTimezone() };
    const { actor, created, field, values } = change;

    return (
        <li className={theme.row}>
            <ActorBadge actor={actor} />
            <div className={theme.content}>
                <div className={theme.headline}>
                    <ChangeSentence actor={actor} field={field} />
                    <time dateTime={created} className={theme.date}>
                        {`${formatDateWithTimezone(created, timezone)}, ${formatTimeAndTimezone(created, timezone)}`}
                    </time>
                </div>
                {values && (
                    <div className={theme.values}>
                        <Tag variant="neutral">{values.from}</Tag>
                        <Svg src={arrowRightSvg} />
                        <Tag variant="lightBlue">{values.to}</Tag>
                    </div>
                )}
            </div>
        </li>
    );
}

interface ActorProps {
    actor: RecentChangeActor;
}

function ActorBadge({ actor }: ActorProps) {
    switch (actor.type) {
        case "user":
            return (
                <span className={theme.avatar} aria-hidden>
                    {initialsOf(actor.name)}
                </span>
            );
        case "automated":
            return (
                <span className={theme.systemTile}>
                    <Svg src={settingsSvg} className={theme.systemIcon} />
                </span>
            );
        default:
            return assertNever(actor);
    }
}

interface ChangeSentenceProps extends ActorProps {
    field: string;
}

function ChangeSentence({ actor, field }: ChangeSentenceProps) {
    switch (actor.type) {
        case "user":
            return (
                <Link
                    to={actor.url}
                    className={cx(theme.sentence, theme.userSentence)}
                >
                    <Trans
                        i18nKey="RecentChanges_UserChange"
                        values={{ name: actor.name, field }}
                    />
                </Link>
            );
        case "automated":
            return (
                <span className={cx(theme.sentence, theme.automatedSentence)}>
                    <Trans
                        i18nKey="RecentChanges_AutomatedChange"
                        values={{ field, reason: actor.reason }}
                    />
                </span>
            );
        default:
            return assertNever(actor);
    }
}

/** "Anders Peter Holm" becomes "AH". */
function initialsOf(name: string) {
    const [first = "", ...rest] = name.trim().split(/\s+/);
    const last = rest.at(-1) ?? "";
    return (first.charAt(0) + last.charAt(0)).toUpperCase();
}
